import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { WA_LOCATION } from '@ng-web-apis/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';

import { RoutesPath } from '@features/loan/enums/routes.enum';
import { Catalog } from '@features/loan/models';
import { PublishablesAreas } from '@features/loan/models/publishables-areas-response.model';
import { LoanService } from '@features/loan/services/loan.service';

import {
  createLoanActions,
  createLoanPostConnectionActions,
  getCatalogActions,
  getPublishablesAreasActions,
  getSimulationForConnectedCustomerActions,
  getSimulationForConnectedCustomerPostConnectionActions,
  getSimulationForNotConnectedUserActions,
  jointCaseCreateLoanActions,
  jointCaseGetSimulationActions,
  mobility4YouActions,
  saveSimulationActions,
} from '../actions';

@Injectable()
export class LoanEffects {
  protected loanService = inject(LoanService);

  protected actions$ = inject(Actions);

  protected router = inject(Router);

  private readonly location: Location = inject(WA_LOCATION);

  public loansListLoad$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getCatalogActions.load),
      switchMap(() => this.loanService.getCatalog()),
      map((catalog: Catalog) => {
        return getCatalogActions.loadCompleted({
          catalog,
        });
      }),
      catchError((error) => of(getCatalogActions.loadFailed({ error }))),
    );
  });

  public publishablesAreasInit$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getCatalogActions.load),
      map(() => {
        return getPublishablesAreasActions.load();
      }),
    );
  });

  public publishablesAreasLoad$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPublishablesAreasActions.load),
      switchMap(() => this.loanService.getPublishablesAreas()),
      map((areas: PublishablesAreas) => {
        return getPublishablesAreasActions.loadCompleted({
          areas,
        });
      }),
    );
  });

  public loanProjectCreation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(createLoanActions.post),
      switchMap(({ loanItemId, userChoices, mobility4YouParams }) =>
        this.loanService.postLoanCreation(loanItemId, false, mobility4YouParams).pipe(
          map((creationResponse) => {
            return createLoanActions.postCompleted({ creationResponse, userChoices });
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return of(
                getSimulationForNotConnectedUserActions.load({
                  loanItemId: loanItemId,
                  userChoices: userChoices,
                }),
              );
            } else {
              return of(
                createLoanActions.postFailed({
                  errorCode: error.status,
                  errorMsg: error.message,
                }),
              );
            }
          }),
        ),
      ),
    );
  });

  public notConnectedSimulation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getSimulationForNotConnectedUserActions.load),
      switchMap(({ loanItemId, userChoices }) =>
        this.loanService.getNotConnectedUserSimulation(loanItemId, userChoices).pipe(
          map((notConnSimulResponse) => {
            return getSimulationForNotConnectedUserActions.loadCompleted({ notConnSimulResponse });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              getSimulationForNotConnectedUserActions.loadFailed({
                error,
              }),
            );
          }),
        ),
      ),
    );
  });

  public redirectToGetSimulationForConnectedCustomerActions$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(createLoanActions.postCompleted),
        switchMap(({ creationResponse, userChoices }) => {
          return of(
            getSimulationForConnectedCustomerActions.load({
              projectId: creationResponse.id,
              userChoices,
            }),
          );
        }),
      );
    },
  );

  public connectedSimulation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getSimulationForConnectedCustomerActions.load),
      switchMap(({ projectId, userChoices }) =>
        this.loanService.getConnectedCustomerSimulation(projectId, userChoices).pipe(
          map((connSimulResponse) => {
            return getSimulationForConnectedCustomerActions.loadCompleted({ connSimulResponse });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              getSimulationForConnectedCustomerActions.loadFailed({
                error,
              }),
            );
          }),
        ),
      ),
    );
  });

  public redirectToStandardErrorPage$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          getSimulationForConnectedCustomerActions.loadFailed,
          getSimulationForNotConnectedUserActions.loadFailed,
          saveSimulationActions.loadFailed,
          createLoanActions.postFailed,
          mobility4YouActions.invalidParams,
          jointCaseCreateLoanActions.loadFailed,
          jointCaseGetSimulationActions.loadFailed,
          createLoanPostConnectionActions.loadFailed,
          getSimulationForConnectedCustomerPostConnectionActions.loadFailed,
          getCatalogActions.loadFailed,
        ),
        tap((action) => {
          let errorCode: string;
          if ('error' in action && action.error instanceof HttpErrorResponse) {
            errorCode = action.error.status.toString();
          } else if ('errorCode' in action) {
            errorCode = action.errorCode.toString();
          } else {
            errorCode = '404';
          }
          const params = { code: errorCode };
          return this.router.navigate([RoutesPath.Error], {
            queryParams: params,
            skipLocationChange: true,
          });
        }),
      );
    },
    { dispatch: false },
  );

  public redirectToSimulationResult$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          getSimulationForConnectedCustomerActions.loadCompleted,
          getSimulationForNotConnectedUserActions.loadCompleted,
        ),
        tap(() => {
          return this.router.navigate([RoutesPath.SimulationResult], {
            skipLocationChange: true,
          });
        }),
      );
    },
    { dispatch: false },
  );

  public sendSimulationForSaving$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveSimulationActions.load),
      switchMap(({ projectId, simulationReference }) =>
        this.loanService.saveConnectedCustomerSimulation(projectId, simulationReference).pipe(
          map((response) => {
            return saveSimulationActions.loadCompleted({ response });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              saveSimulationActions.loadFailed({
                error,
              }),
            );
          }),
        ),
      ),
    );
  });

  public jointCaseLoanProjectCreation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(jointCaseCreateLoanActions.load),
      switchMap(({ loanItemId, userChoices }) =>
        this.loanService.postLoanCreation(loanItemId, true).pipe(
          map((creationResponse) => {
            return jointCaseCreateLoanActions.loadCompleted({
              creationResponse,
              userChoices,
            });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              jointCaseCreateLoanActions.loadFailed({
                errorCode: error.status,
                errorMsg: error.message,
              }),
            );
          }),
        ),
      ),
    );
  });

  public jointCaseLoanProjectCreated$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(jointCaseCreateLoanActions.loadCompleted),
      switchMap(({ creationResponse, userChoices }) => {
        return of(
          jointCaseGetSimulationActions.load({
            projectId: creationResponse.id,
            userChoices,
          }),
        );
      }),
    );
  });

  public jointCaseGetSimulation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(jointCaseGetSimulationActions.load),
      switchMap(({ projectId, userChoices }) =>
        this.loanService.getConnectedCustomerSimulation(projectId, userChoices).pipe(
          map((connSimulResponse) => {
            return jointCaseGetSimulationActions.loadCompleted({ connSimulResponse });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              jointCaseGetSimulationActions.loadFailed({
                error,
              }),
            );
          }),
        ),
      ),
    );
  });

  public loanProjectCreationPostConnection$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(createLoanPostConnectionActions.load),
      switchMap(({ loanItemId, userChoices }) =>
        this.loanService.postLoanCreation(loanItemId, false).pipe(
          map((creationResponse) => {
            return createLoanPostConnectionActions.loadCompleted({ creationResponse, userChoices });
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return of(createLoanPostConnectionActions.loadFailed401());
            } else {
              return of(
                createLoanPostConnectionActions.loadFailed({
                  errorCode: error.status,
                  errorMsg: error.message,
                }),
              );
            }
          }),
        ),
      ),
    );
  });

  public reloadApp$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(createLoanPostConnectionActions.loadFailed401),
        tap(() => {
          return this.location.reload();
        }),
      );
    },
    { dispatch: false },
  );

  public redirectToGetSimulationForConnectedCustomerPostConnectionActions$: Observable<Action> =
    createEffect(() => {
      return this.actions$.pipe(
        ofType(createLoanPostConnectionActions.loadCompleted),
        switchMap(({ creationResponse, userChoices }) => {
          return of(
            getSimulationForConnectedCustomerPostConnectionActions.load({
              projectId: creationResponse.id,
              userChoices,
            }),
          );
        }),
      );
    });

  public connectedSimulationPostConnection$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getSimulationForConnectedCustomerPostConnectionActions.load),
      switchMap(({ projectId, userChoices }) =>
        this.loanService.getConnectedCustomerSimulation(projectId, userChoices).pipe(
          map((connSimulResponse) => {
            return getSimulationForConnectedCustomerPostConnectionActions.loadCompleted({
              connSimulResponse,
            });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              getSimulationForConnectedCustomerPostConnectionActions.loadFailed({
                error,
              }),
            );
          }),
        ),
      ),
    );
  });
}
